/*
 * Deskon - additions to the cart drawer.
 *
 * WHAT THIS FILE DOES. It places three things the theme has no equivalent for - the savings pill,
 * the payment and guarantee footer, the empty state - reports whether the cart list has more to
 * scroll, and animates lines as they arrive.
 *
 * ------------------------------------------------------------------------------------------------
 * IT USED TO PLACE A FOURTH, AND THAT ONE IS GONE FOR GOOD.
 *
 * The upsell card - "Chosen by 89% of customers" plus a recommended product and an Add button -
 * was cloned here from a <template> in snippets/cart-products.liquid by mountUpsell(), kept honest
 * by syncUpsell(), added to the cart by addUpsell() and re-rendered by refreshCart(). All four
 * functions have been deleted, along with the template and assets/deskon-cart-upsell.css.
 *
 * Its slot above the subtotal now holds the multibuy progress bar, which is written in Liquid in
 * snippets/cart-summary.liquid. THAT IS A DELIBERATE MOVE OFF SCRIPT AND ONTO THE SERVER: the card
 * needed two scripts precisely because its markup was cloned once and then went stale on every
 * cart change. A block rendered inside <cart-items-component> is re-rendered by the theme itself,
 * so it cannot disagree with the cart, and morphing animates its width instead of replacing it.
 *
 * DO NOT REINTRODUCE A refreshCart()-STYLE HELPER FOR IT. That function replaced the whole
 * component with comp.replaceWith(fresh), which throws away the element the bar's CSS transition
 * animates - the bar would jump rather than fill.
 * ------------------------------------------------------------------------------------------------
 * EMPTINESS IS READ FROM THE DOM, NOT FROM THE DATA ISLAND, AND THAT IS NOT BELT AND BRACES.
 *
 * The island used to be emitted outside the cart section, so morphSection() never refreshed it.
 * Emptying the cart left itemCount at its old value; mountEmpty() saw a non-zero count and
 * returned, and the drawer kept fragments of the previous render on screen. The island has since
 * moved into snippets/cart-products.liquid, inside the section, so it is correct now - but
 * emptiness is still taken from the markup, because the markup is the thing the customer is
 * looking at and it cannot disagree with itself.
 *
 * THE THEME SWAPS THE DRAWER'S MARKUP WHOLESALE WHEN THE CART EMPTIES: .cart-drawer__summary stops
 * existing altogether and an .cart-drawer__heading--empty appears. Anything that mounts into the
 * summary simply finds no anchor and does nothing, which is correct.
 * ------------------------------------------------------------------------------------------------
 *
 * EVERYTHING IS RE-APPLIED AFTER EVERY CART CHANGE. component-cart-items.js calls morphSection()
 * on each quantity change, which replaces the section's DOM and takes any injected node with it.
 * The observer below is the safety net; apply() is idempotent.
 */

(function () {
  'use strict';

  var TAG = '[deskon-cart]';
  var OFFER_DAYS_AHEAD = 2;
  // Unused since 17/10 (line reads 'on selected lamps').
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  var GUARANTEE_TEXT = 'Love it or return it';

  /*
   * THE SOCIAL-PROOF ITEM BESIDE THE GUARANTEE (asked 22/10/2026). Same figure as the product page's
   * reassurance line under Add to cart - the Buy buttons block's third text, reassurance_text_3 -
   * but typed here a second time, because a drawer script cannot read a block setting. IF THE FIGURE
   * EVER CHANGES, CHANGE IT IN BOTH PLACES.
   *
   * THE HEART IS FILLED, unlike the outline heart on the product page: at 12px in a row of 11px grey
   * text an outline in rose-red reads as a speck, a filled shape reads as a heart. Its colour and
   * spacing are in assets/deskon-cart-bottom.css (.dk-cart__heart), not here.
   */
  var CUSTOMERS_TEXT = '12,000+ customers';

  var HEART = '<svg class="dk-cart__heart" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">' +
    '<path d="M12 20.6 4.5 13.3a4.8 4.8 0 0 1 6.8-6.8l.7.7.7-.7a4.8 4.8 0 0 1 6.8 6.8Z" fill="currentColor"/></svg>';

  var CHECK = '<svg viewBox="0 0 13 13" width="13" height="13" fill="none" aria-hidden="true">' +
    '<circle cx="6.5" cy="6.5" r="5.2" stroke="currentColor" stroke-width="1.4"/>' +
    '<path d="M4.2 6.6 5.9 8.3 9 4.9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function el(tag, cls, id) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (id) n.id = id;
    return n;
  }

  function frag(markup) {
    var t = document.createElement('template');
    t.innerHTML = markup;
    return t.content.firstElementChild;
  }

  function data() {
    var tag = document.getElementById('deskon-cart-data');
    if (!tag) return null;
    try { return JSON.parse(tag.textContent); } catch (e) {
      console.warn(TAG, 'cart data is not valid JSON', e);
      return null;
    }
  }

  function drawer() { return document.querySelector('cart-drawer-component'); }

  function panel() {
    var host = drawer();
    if (!host) return null;
    return host.querySelector('dialog') || host;
  }

  function lineKeys() {
    return [].map.call(document.querySelectorAll('.dk-line'), function (n) {
      return n.getAttribute('data-key');
    });
  }

  /*
   * Two independent signals, either of which is conclusive: the theme's own empty heading, and the
   * absence of both cart lines and the cart form. Requiring BOTH halves of the second test matters
   * during a morph, when the DOM is briefly mid-swap - a check on lines alone would flash the empty
   * state between two renders.
   */
  function isEmpty() {
    var host = panel();
    if (!host) return true;
    if (host.querySelector('.cart-drawer__heading--empty')) return true;
    return !document.querySelector('.dk-line') && !document.getElementById('cart-form');
  }

  function offerDate() {
    var d = new Date();
    d.setDate(d.getDate() + OFFER_DAYS_AHEAD);
    return d.getDate() + ' ' + MONTHS[d.getMonth()];
  }

  /* ------------------------------------------------------- lines arriving */

  /*
   * THE MIRROR OF .removing, WHICH IS THE THEME'S. Nothing marks an arriving line, which is why
   * adding to the cart looked abrupt beside the removal animation.
   *
   * IT CANNOT SIMPLY ANIMATE EVERY NEW NODE: adding re-renders the whole section, so EVERY line is
   * a new element each time. The keys present a moment ago are remembered, and only a line whose
   * data-key was not among them is animated. THE FIRST PASS ONLY SEEDS THE SET - otherwise opening
   * the drawer would unfold every line in the cart.
   *
   * THE HEIGHT IS MEASURED BEFORE THE CLASS GOES ON, because height cannot animate from 0 to auto.
   */
  var knownKeys = null;

  function animateArrivals() {
    var lines = document.querySelectorAll('.dk-line');
    var keys = lineKeys();

    if (knownKeys === null) { knownKeys = keys; return; }

    [].forEach.call(lines, function (line) {
      var key = line.getAttribute('data-key');
      if (!key || knownKeys.indexOf(key) !== -1) return;
      if (line.dataset.dkEntered === 'true') return;

      line.dataset.dkEntered = 'true';
      line.style.setProperty('--dk-enter-height', line.offsetHeight + 'px');
      line.classList.add('dk-line--enter');

      line.addEventListener('animationend', function done() {
        line.removeEventListener('animationend', done);
        line.classList.remove('dk-line--enter');
        line.style.removeProperty('--dk-enter-height');
      });
    });

    knownKeys = keys;
  }

  /* ------------------------------------------------------------- scroll state */

  function scroller() {
    var host = panel();
    return host ? host.querySelector('.cart-drawer__content') : null;
  }

  function readScroll() {
    var host = panel();
    var box = scroller();
    if (!host || !box) return;

    var more = box.scrollHeight - box.clientHeight > 4 &&
      box.scrollTop + box.clientHeight < box.scrollHeight - 4;

    host.setAttribute('data-dk-scroll', more ? 'more' : 'end');
  }

  function watchScroll() {
    var box = scroller();
    if (!box) return;
    if (box.dataset.dkScrollBound !== 'true') {
      box.dataset.dkScrollBound = 'true';
      box.addEventListener('scroll', readScroll, { passive: true });
    }
    readScroll();
  }

  /* ------------------------------------------------------- savings, reassurance */

  /*
   * The figure comes from the island, which is emitted inside the cart section and therefore
   * recomputed by the server on every change. If it is ever moved back out, this pill starts lying.
   *
   * IT IS REMOVED WHEN THERE IS NOTHING TO SAY, not merely skipped, so a saving that drops to zero
   * takes the pill with it instead of leaving the last one on screen.
   *
   * IT MOUNTS INTO THE TOTAL ROW, NOT INTO THE SUMMARY. Appended to .cart-drawer__summary it landed
   * last of all - under the checkout button, the payment marks and the guarantee line - and
   * deskon-cart-watch.js drops and re-mounts it on every value change, so moving it by hand would
   * not have survived the next quantity change. The anchor is .cart__total and the pill goes
   * immediately before .cart__total-value.
   *
   * THE PARENT IS CHECKED, NOT MERELY THE EXISTENCE, exactly as mountFooter does. A pill still
   * sitting in the old place would have its text refreshed for ever and never move.
   */
  function mountSavings(info, empty) {
    var host = panel();
    if (!host) return;

    var existing = document.getElementById('deskon-cart-savings');
    var show = !empty && info && info.savingsRaw;

    if (!show) { if (existing) existing.remove(); return; }

    var summary = host.querySelector('.cart-drawer__summary');
    var row = summary ? summary.querySelector('.cart__total') : null;

    /* Mid-morph the row can be missing for a frame. Doing nothing beats removing and re-adding,
       which would flicker the pill on every quantity change. */
    if (!row) return;

    /* Non-breaking space before the amount, and nowrap in CSS: the label and the figure are one
       phrase and must never be split across two lines inside a 21px pill. */
    var label = (info.savingsLabel || 'You save') + ' ' + info.savings;

    if (existing && existing.parentElement === row) {
      /*
       * THE COMPARISON IS NOT AN OPTIMISATION. IT IS THE FIX FOR A LOOP THAT COST THE WHOLE SITE
       * HALF ITS FRAMES.
       *
       * Assigning textContent ALWAYS removes the old text node and inserts a new one, even when
       * the string is identical - the DOM has no "same value, do nothing" path. That counts as a
       * childList mutation on this span.
       *
       * deskon-cart-watch.js watches document.body with { childList: true, subtree: true } and
       * answers every mutation by scheduling another apply() on the next animation frame. So this
       * one line fed itself: write -> mutation -> observer -> next frame -> write. Measured on the
       * live product page it ran 57 times a second, for the life of the page, on EVERY page of the
       * site - the drawer is in the header. apply() costs about 15.7ms a pass against a 16.7ms
       * frame budget, so the browser was locked at roughly 30fps doing nothing at all.
       *
       * That is what made every animation on this site look like it was stepping - the accordions
       * on the product page were only the place it was noticed. There is nothing wrong with an
       * animation that gets half the frames it asks for; it just cannot be smooth.
       *
       * deskon-cart-watch.js now also switches its observer off while it works, so a future
       * unconditional write cannot bring the loop back. Both halves are deliberate: this one keeps
       * the DOM quiet, that one keeps the observer honest. DO NOT DROP EITHER.
       */
      if (existing.textContent !== label) existing.textContent = label;
      return;
    }
    if (existing) existing.remove();

    var pill = el('span', 'dk-cart__savings', 'deskon-cart-savings');
    pill.textContent = label;

    /* A null reference node appends, which is the right fallback if the theme ever renames the
       value element: the pill stays in the total row rather than disappearing. */
    row.insertBefore(pill, row.querySelector('.cart__total-value'));
  }

  function paymentRow() {
    var tpl = document.getElementById('deskon-cart-payment');
    var row = el('div', 'dk-cart__pay');
    if (tpl && tpl.content) row.appendChild(tpl.content.cloneNode(true));
    return row;
  }

  function guaranteeRow() {
    var row = el('p', 'dk-cart__guarantee');
    row.appendChild(frag(CHECK));
    var text = el('span');
    text.textContent = GUARANTEE_TEXT;
    row.appendChild(text);
    /* Same line, to the right of the guarantee: heart + customer count. */
    row.appendChild(frag(HEART));
    var customers = el('span', 'dk-cart__customers');
    customers.textContent = CUSTOMERS_TEXT;
    row.appendChild(customers);
    return row;
  }

  function mountFooter() {
    var host = panel();
    if (!host) return;

    var summary = host.querySelector('.cart-drawer__summary');
    var anchor = summary || host.querySelector('.cart-drawer__content');
    if (!anchor) return;

    var existing = document.getElementById('deskon-cart-footer');

    /* The summary disappears when the cart empties and comes back when it refills, so the footer
       has to move with it - not just be created once. Left where it was, it would sit in the
       scrolling area on a refilled cart. */
    if (existing) {
      if (existing.parentElement === anchor) return;
      existing.remove();
    }

    var foot = el('div', 'dk-cart__foot', 'deskon-cart-footer');
    foot.appendChild(paymentRow());
    foot.appendChild(guaranteeRow());
    anchor.appendChild(foot);
  }

  /* ------------------------------------------------------------- empty state */

  function mountEmpty(empty) {
    var host = panel();
    if (!host) return;

    var existing = document.getElementById('deskon-cart-empty');

    if (!empty) { if (existing) existing.remove(); return; }
    if (existing) return;

    var content = host.querySelector('.cart-drawer__content');
    if (!content) return;

    var block = el('div', 'dk-cart__empty', 'deskon-cart-empty');

    var btn = el('a', 'dk-cart__empty-btn');
    btn.href = '/collections/best-sellers';
    btn.textContent = 'Discover our bestsellers';
    block.appendChild(btn);

    var offer = el('p', 'dk-cart__empty-offer');
    var pct = el('span');
    pct.textContent = '−30%';
    var until = el('span', 'dk-cart__empty-until');
    until.textContent = 'on selected lamps';
    offer.appendChild(pct);
    offer.appendChild(until);
    block.appendChild(offer);

    /* Before the footer, so the button and the offer line sit under the heading rather than under
       the payment marks. */
    var foot = document.getElementById('deskon-cart-footer');
    if (foot && foot.parentElement === content) {
      content.insertBefore(block, foot);
    } else {
      content.appendChild(block);
    }
  }

  /* ------------------------------------------------------------------ apply */

  function apply() {
    var host = panel();
    if (!host) return false;

    var empty = isEmpty();
    var info = data();

    host.classList.add('dk-cart');
    host.setAttribute('data-dk-empty', empty ? 'true' : 'false');

    animateArrivals();
    mountSavings(info, empty);
    mountFooter();
    mountEmpty(empty);
    watchScroll();
    return true;
  }

  [0, 80, 300, 900].forEach(function (d) {
    if (d === 0) apply(); else setTimeout(apply, d);
  });

  window.addEventListener('load', apply);
  window.addEventListener('resize', readScroll, { passive: true });
  document.addEventListener('shopify:section:load', function () { setTimeout(apply, 0); });

  /* morphSection replaces the section wholesale, so watch the drawer rather than the section. */
  var host = drawer();
  if (host && window.MutationObserver) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; apply(); });
    }).observe(host, { childList: true, subtree: true });
  }

  window.deskonCart = {
    apply: apply,
    debug: function () {
      var host = panel();
      var bar = document.querySelector('.dk-multibuy');
      return {
        hasDrawer: !!drawer(),
        hasPanel: !!host,
        hasData: !!document.getElementById('deskon-cart-data'),
        islandCount: document.querySelectorAll('#deskon-cart-data').length,
        hasSummary: !!(host && host.querySelector('.cart-drawer__summary')),
        savingsInTotalRow: !!document.querySelector('.cart__total > #deskon-cart-savings'),
        isEmpty: isEmpty(),
        multibuyState: bar ? bar.getAttribute('data-dk-multibuy') : null,
        multibuyFill: bar ? bar.querySelector('.dk-multibuy__fill').style.width : null,
        lineKeys: lineKeys(),
        scrollState: host ? host.getAttribute('data-dk-scroll') : null,
        data: data(),
      };
    },
  };
})();